import type { EmployeeListFilterStatus } from '../../services/employee';

type StatusFilter = EmployeeListFilterStatus | 'all';

/**
 * Query keys for consistent cache management
 * Centralized query key definitions for the application
 */

export const queryKeys = {
  auth: {
    user: ['auth', 'user'] as const,
  },
  company: {
    all: ['company'] as const,
    branches: (companyId: number) => ['companyBranch', companyId] as const,
    defaultBranch: (companyId: number) => ['companyBranch', companyId, 'default'] as const,
    detail: () => ['company', 'detail'] as const,
    /** `get-my-company-detail` sent with the native request keys — the only
     *  variant whose response carries `today_attendance`. Kept off `detail()`
     *  so the payload-less callers can't serve it from cache. */
    todayAttendance: (params: {
      userId: number;
      timezone: string;
      userDeviceId: string;
    }) => ['company', 'detail', 'todayAttendance', params] as const,
    branchWifis: (params: { companyId: number; branchId: number }) =>
      ['companyBranch', 'wifis', params] as const,
  },
  employee: {
    list: (params: {
      companyId: number;
      branchId: number | -1;
      status: StatusFilter;
      search?: string;
      page?: number;
      limit?: number;
      sortBy?: string;
      sortOrder?: 'ASC' | 'DESC';
      userId?: number;
      userTimeZone?: string;
      userDeviceId?: string;
    }) => ['employee', 'list', params] as const,
    detail: (params: {
      employeeId: number;
      companyId: number;
      startDate: string;
      endDate: string;
      userId?: number;
    }) => ['employee', 'detail', params] as const,
    scoreDetails: (params: {
      employeeId: number;
      companyId: number;
      deviceId?: string;
      userId?: number;
    }) => ['employee', 'score-details', params] as const,
    byRole: (params: {
      companyId: number;
      roleIds: string;
      userId?: number;
      excludeCompanyEmployeeId?: number;
    }) => ['employee', 'by-role', params] as const,
  },
  performanceReview: {
    employees: (params: {
      companyId: number;
      branchId: number;
      employeeId: number;
    }) => ['performance-review', 'employees', params] as const,
    questions: (params: {
      companyId: number;
      employeeId: number;
    }) => ['performance-review', 'questions', params] as const,
  },
  announcement: {
    list: (params: { companyId: number; branchId: number }) =>
      ['announcement', 'list', params] as const,
  },
  attendance: {
    byStatus: (params: {
      companyId: number;
      branchId: number | -1;
      startDate: string;
      endDate: string;
      filterBy?: string | 'all';
      pageNo?: number;
      pageLimit?: number;
    }) => ['attendance', 'byStatus', params] as const,
    byEmployee: (params: {
      companyId: number;
      branchId: number | -1;
      userId: number;
      employeeId: number;
      startDate: string;
      endDate: string;
    }) => ['attendance', 'byEmployee', params] as const,
    log: (params: {
      userId: number;
      companyId: number;
      branchId: number;
      employeeId: number;
      startDate: string;
      endDate: string;
    }) => ['attendance', 'log', params] as const,
  },
  tracking: {
    branchEmployeeLocations: (params: {
      companyId: number;
      branchId: number | -1;
      duration: number;
      localDate: string;
    }) => ['tracking', 'branchEmployeeLocations', params] as const,
    activeEmployeesByBranch: (params: {
      companyId: number;
      branchId: number | -1;
      localDate: string;
      lookbackDays: number;
    }) => ['tracking', 'activeEmployeesByBranch', params] as const,
    employeeLocations: (params: {
      employeeId: string;
      companyId: number;
      duration: number;
      localDate: string;
    }) => ['tracking', 'employeeLocations', params] as const,
    // Free reverse-geocode of a coordinate; keyed by rounded lat/lng so nearby
    // points share one cache entry and the same spot is only fetched once.
    reverseGeocode: (lat: number, lng: number) =>
      ['tracking', 'reverseGeocode', lat, lng] as const,
  },
  notes: {
    list: (params: {
      page_limit?: number;
      pagination?: string;
      page_no?: number;
      search?: string;
      start_date?: string;
      end_date?: string;
      sort_field?: string;
      sort_direction?: 'ASC' | 'DESC';
      self_note_id?: string;
    }) => ['notes', 'list', params] as const,
    reminders: (params: {
      page_limit?: number;
      pagination?: string;
      page_no?: number;
      start_date?: string;
      end_date?: string;
    }) => ['notes', 'reminders', params] as const,
  },
  meeting: {
    list: (params: {
      pagination?: string;
      page_limit?: number;
      last_sync_time?: number;
      page_no?: number;
      meeting_id?: number;
    }) => ['meeting', 'list', params] as const,
    detail: (meetingId: number) => ['meeting', 'detail', meetingId] as const,
    activity: (meetingId: number, userId: number) =>
      ['meeting', 'activity', meetingId, userId] as const,
    muteSettings: () => ['meeting', 'muteSettings'] as const,
    /** Warms the shared meeting store for the tracking timeline (own-view). */
    timelineSync: () => ['meeting', 'timeline-sync'] as const,
  },
  task: {
    /** Same shape as meeting-list: stable key (no last_sync_time). */
    list: (params: {
      pagination?: string;
      page_limit?: number;
      page_no?: number;
    }) => ['task', 'list', params] as const,
    activity: (taskId: number, userId: number, groupId: number) =>
      ['task', 'activity', taskId, userId, groupId] as const,
    muteSettings: () => ['task', 'muteSettings'] as const,
    /** Company-wide task analytics dashboard for one specific branch + period. */
    dashboard: (params: {
      companyId: number;
      branchId: number;
      period: string;
    }) => ['task', 'dashboard', params] as const,
  },
  holiday: {
    list: (params: { companyId: number; branchId: number }) =>
      ['holiday', 'list', params] as const,
  },
  approval: {
    list: (params: {
      companyId: number;
      branchId: number;
      pending: number;
      mediaType?: string;
      startDate: string;
      endDate: string;
    }) => ['approval', 'list', params] as const,
    /**
     * The self-scoped list. No branch member: the endpoint takes no branch,
     * and the user is resolved from the token rather than from a parameter.
     */
    myList: (params: {
      companyId: number;
      pending: number;
      startDate: string;
      endDate: string;
      mediaType?: string;
    }) => ['approval', 'myList', params] as const,
    /** Approvals this user may still file a bill against. */
    linkable: (params: { companyId: number }) =>
      ['approval', 'linkable', params] as const,
    /**
     * The bills this user filed. Separate from `myList`: bills come off
     * `get-company-bill-list`, which the approval endpoint cannot serve, and
     * the reply is narrowed to the caller, so the user is part of the key.
     */
    myBills: (params: {
      companyId: number;
      userId: number;
      startDate: string;
      endDate: string;
    }) => ['approval', 'myBills', params] as const,
  },
  payments: {
    /**
     * The whole payables feed for a status bucket. `kind` is deliberately NOT a
     * key member: the feed is always fetched with kind=all and split
     * client-side, so every kind's count stays visible at once.
     */
    feed: (params: { companyId: number; branchId: number; status: 'to_pay' | 'paid' }) =>
      ['payments', 'feed', params] as const,
    /**
     * `search` and `sortOrder` are key members because the server does the
     * filtering and ordering — omitting them would serve one term's results
     * for another. The tab badge passes neither, so its unfiltered count keeps
     * its own entry.
     */
    vendors: (params: { companyId: number; search?: string; sortOrder?: 'asc' | 'desc' }) =>
      ['payments', 'vendors', params] as const,
    vendorDetail: (params: { companyId: number; vendorId: number }) =>
      ['payments', 'vendorDetail', params] as const,
  },
  chat: {
    list: (params: { userId: number; lastSyncTime?: number | null }) =>
      ['chat', 'list', params] as const,
    messages: (params: { userId: number; roomId: string }) =>
      ['chat', 'messages', params] as const,
    /** List of users the current user has blocked. */
    blockedUsers: () => ['chat', 'blockedUsers'] as const,
    /** Full profile + per-contact note for another user. */
    contactProfile: (userId: number) =>
      ['chat', 'contactProfile', userId] as const,
    /** Server-backed per-conversation mute settings. */
    muteSettings: () => ['chat', 'muteSettings'] as const,
    /** Sub-thread (task/meet card descendants) by parent chat_id. */
    subThread: (params: { chatId: number; page?: number }) =>
      ['chat', 'subThread', params] as const,
    /** Server-side unread count for a chat-id range (optionally per peer). */
    unreadCount: (params: {
      startChatId: number;
      endChatId: number;
      toUserId?: number;
    }) => ['chat', 'unreadCount', params] as const,
    /** Chat history for a converted lead (CRM handoff flow). */
    convertedLeadHistory: (params: {
      companyId: number;
      branchId: number;
      toCompanyId: number;
      companyLeadId?: number;
    }) => ['chat', 'convertedLeadHistory', params] as const,
  },
  group: {
    list: () => ['group', 'list'] as const,
    members: (groupId: number) => ['group', 'members', groupId] as const,
  },
  contact: {
    list: (params: {
      page: number;
      size: number;
      search?: string;
      tag_id?: number;
      sort_by?: string;
      sort_order?: 'asc' | 'desc';
      status?: string;
    }) => ['contact', 'list', params] as const,
    /**
     * Stable key for the COMPLETE contact list — the locally-cached
     * "contact DB". Persisted to IndexedDB so it survives reloads.
     */
    all: () => ['contact', 'all'] as const,
  },
  leave: {
    summary: (params: {
      userId: number;
      companyId: number;
      companyEmployeeId: number;
      userTimeZone?: string;
      authUserId?: number;
    }) => ['leave', 'summary', params] as const,
    companyLeaveList: (params: {
      companyId: number;
      branchId: number;
      startDate: string;
      endDate: string;
    }) => ['leave', 'companyLeaveList', params] as const,
  },
  salary: {
    slip: (params: {
      userId: number;
      companyId: number;
      branchId: number;
      companyEmployeeId: number;
    }) => ['salary', 'slip', params] as const,
  },
  request: {
    submit: ['request', 'submit'] as const,
  },
  statistics: {
    count: (params: {
      companyId: number;
      branchId?: number;
      startDate: string;
      endDate: string;
    }) => ['statistics', 'count', params] as const,
  },
  subscription: {
    transaction: () => ['subscription', 'transaction'] as const,
    /**
     * The company-create check reads the same endpoint but needs its own entry.
     * A query holds one set of fetch options, taken from whichever reader last
     * set them, and this endpoint has several readers -- one of which asks for
     * no retries at all. Sharing meant the retry this check relies on happened
     * or did not depending on which reader had most recently touched the query,
     * which is not something to leave to chance on a check that decides whether
     * someone may start a company.
     */
    createCompanyGate: () => ['subscription', 'transaction', 'create-company-gate'] as const,
  },
  drive: {
    /** folderId: undefined = root, number = list that folder's contents */
    list: (folderId: number | undefined) =>
      ['drive', 'list', folderId === undefined ? 'root' : folderId] as const,
  },
  userProfile: {
    detail: (userId: number) => ['userProfile', 'detail', userId] as const,
  },
  tag: {
    list: () => ['tag', 'list'] as const,
  },
  contactGroup: {
    list: () => ['contactGroup', 'list'] as const,
  },
  checklist: {
    /** Current user's saved checklist templates (get-check-list, user-scoped). */
    templates: () => ['checklist', 'templates'] as const,
  },
  companyLead: {
    /** Every companyLead query — the prefix to invalidate after a client write. */
    all: () => ['companyLead'] as const,
    /** Current user's client list for a company = their converted sales leads. */
    clients: (companyId: number, userId: number) =>
      ['companyLead', 'clients', companyId, userId] as const,
  },
  client: {
    /**
     * Every client query. A client write also changes the converted-leads view
     * (each register client carries a mirror lead), so both prefixes are
     * invalidated together after any mutation.
     */
    all: () => ['client'] as const,
    /**
     * Client Register list (Active / Past). Every filter is a key member
     * because the SERVER does the searching, sorting and paging — omitting one
     * would serve another query's results.
     */
    list: (params: {
      companyId: number;
      branchId: number | -1;
      status: 'active' | 'exited';
      search?: string;
      source?: 'all' | 'lead' | 'manual';
      sortBy?: string;
      sortOrder?: 'ASC' | 'DESC';
      pageNo?: number;
      pageLimit?: number;
    }) => ['client', 'list', params] as const,
    detail: (params: { companyId: number; clientId: number }) =>
      ['client', 'detail', params] as const,
  },
  commonCompany: {
    list: (userId: number, toUserId: number) =>
      ['commonCompany', userId, toUserId] as const,
    /** Aggregate shared-company map for a whole contact set (deferred + cached). */
    contactMap: (userId: number, contactIdsKey: string) =>
      ['commonCompany', 'contactMap', userId, contactIdsKey] as const,
  },
  integration: {
    list: (companyId: number) => ['integration', 'list', companyId] as const,
    detail: (companyId: number, slug: string) =>
      ['integration', 'detail', companyId, slug] as const,
  },
  hrmsOverview: {
    tasksMeetsCard: (userId: number, companyId: number) =>
      ['hrms-overview', 'tasks-meets-card', userId, companyId] as const,
    productivityCard: (userId: number, companyId: number, month: string) =>
      ['hrms-overview', 'productivity-card', userId, companyId, month] as const,
    teamProductivity: (userId: number, companyId: number) =>
      ['hrms-overview', 'team-productivity', userId, companyId] as const,
    frequentAbsences: (
      userId: number,
      companyId: number,
      branchId: number,
      month: string
    ) =>
      ['hrms-overview', 'frequent-absences', userId, companyId, branchId, month] as const,
    lateArrivals: (
      userId: number,
      companyId: number,
      branchId: number,
      month: string
    ) =>
      ['hrms-overview', 'late-arrivals', userId, companyId, branchId, month] as const,
    performanceIssue: (userId: number, companyId: number, branchId: number) =>
      ['hrms-overview', 'performance-issue', userId, companyId, branchId] as const,
    probationEnding: (userId: number, companyId: number, branchId: number) =>
      ['hrms-overview', 'probation-ending', userId, companyId, branchId] as const,
    documentVerification: (userId: number, companyId: number, branchId: number) =>
      ['hrms-overview', 'document-verification', userId, companyId, branchId] as const,
  },
  handbook: {
    detail: (userId: number, companyId: number) =>
      ['handbook', 'detail', userId, companyId] as const,
  },
  airports: {
    cities: (query: string, isoCountry: string) =>
      ['airports', 'cities', query, isoCountry] as const,
    /**
     * Nearest named airport to a fix -- static reference data, cached hard.
     *
     * The coordinates go in raw. Every factory here is a pure passthrough, and
     * the consistency suite calls each one with no arguments to prove it, so a
     * key that transforms its inputs (`lat.toFixed(3)`) throws there.
     */
    nearest: (lat: number, lng: number) => ['airports', 'nearest', lat, lng] as const,
  },
  advanceSalaryRules: {
    /** Branch policy + this employee's pay + any advance still being repaid. */
    detail: (companyId: number) => ['advance-salary-rules', 'detail', companyId] as const,
  },
  expensePolicy: {
    detail: (companyId: number) => ['expense-policy', 'detail', companyId] as const,
  },
  expenseLedger: {
    /**
     * One employee's advance-vs-claims statement. `companyEmployeeId` is a key
     * member because the same endpoint serves another employee's ledger to an
     * authority-1 viewer; 0 means the signed-in employee's own.
     */
    statement: (params: { companyId: number; companyEmployeeId: number }) =>
      ['expense-ledger', 'statement', params] as const,
  },
} as const;

